import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Paper,
  Grid,
  Select,
  useMediaQuery,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/DeleteOutline";

import AddIcon from "@mui/icons-material/Add";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  where,
  query,
} from "firebase/firestore";
import { db } from "./firebase";
import { DashedPaper } from "../StyledComponents";
import CreateIcon from "@mui/icons-material/Create";
import { lighten, darken } from "@mui/material/styles";
import { useSnackbar } from "./SnackbarHandler";
import LanguageSelector from "./LanguageSelector";
import LoadingDialog from "./LoadingDialog";
import { alpha } from "@mui/material/styles";

import { Link, useLocation } from "react-router-dom";
import WelcomeDialogLessons from "./WelcomeDialogLessons";

import { getStyles } from "../index";
import SegmentIcon from "@mui/icons-material/Segment";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AnimatedStep } from "./Animations";

import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import ThemeFlowBox from "./ThemeFlowBox";

import { useNavigate } from "react-router-dom";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { explanatoryTextRubrics } from "./forklarandeText";
import { callApi } from "./api";
import { argumentativeTextRubrics } from "./argumenterandeText";
import { descriptiveTextRubrics } from "./beskrivandeText";
import { berattandeTextRubrics } from "./berattandeText";
import { InstructionItem, trackLesson } from "./functions";
import { instruerandeTextRubrics } from "./instruerandeText";
import { informerandeTextRubrics } from "./informerandeText";

import { oldStandardRubrics } from "./oldStandardRubrics";

const Rubrics = ({ user, setMinimized }) => {
  const [rubricName, setRubricName] = useState("");
  const [criteria, setCriteria] = useState([
    {
      name: "",
      descriptors: [
        { text: "", grade: "A" },
        { text: "", grade: "C" },
        { text: "", grade: "E" },
        { text: "", grade: "F" },
      ],
    },
  ]);
  const { showSnackbar } = useSnackbar();
  const [isExample, setIsExample] = useState(false);
  const theme = useTheme();
  const styles = getStyles(theme);
  const [isSaving, setIsSaving] = useState(false);
  const [view, setView] = useState("selection"); // "selection", "aiForm", or "form"
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [language, setLanguage] = useState("svenska");

  const [assignmentName, setAssignmentName] = useState("");
  const [assignmentDescription, setAssignmentDescription] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();

  const location = useLocation();
  const isFormative =
    new URLSearchParams(location.search).get("ref") === "formative";

  // 3. Add this useEffect after your other useEffects:
  useEffect(() => {
    if (view === "form") {
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [view]);

  const handleAddAllRubrics = async () => {
    const rubricsToAdd = [
      ...explanatoryTextRubrics,
      ...argumentativeTextRubrics,
      ...descriptiveTextRubrics,
      ...berattandeTextRubrics,
      ...instruerandeTextRubrics,
      ...informerandeTextRubrics,
    ];

    try {
      setIsSaving(true);
      for (const rubric of rubricsToAdd) {
        await addDoc(collection(db, "rubrics"), rubric);
      }
      showSnackbar("Alla bedömningsmatriser har sparats", "success");
    } catch (error) {
      console.error("Error saving rubrics:", error);
      showSnackbar(
        "Ett fel uppstod när bedömningsmatriserna skulle sparas",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      A: "#2E7D32",
      C: "#FFA000",
      E: "#D32F2F",
      F: "#757575",
    };

    switch (grade) {
      case "A":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.A, 0.1);
      case "C":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.C, 0.1);
      case "E":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.E, 0.1);
      case "F":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.F, 0.1);
      default:
        return theme.palette.grey[100];
    }
  };

  const handleDeleteAllRubrics = async () => {
    try {
      setIsSaving(true);
      const rubricsRef = collection(db, "rubrics");
      const querySnapshot = await getDocs(
        query(
          rubricsRef,
          where("userId", "==", "AJbQ95xGjLSZd3GpS3j41Uv73yf2"),
        ),
      );

      for (const doc of querySnapshot.docs) {
        await deleteDoc(doc.ref);
      }

      showSnackbar("Alla bedömningsmatriser har raderats", "success");
    } catch (error) {
      console.error("Error deleting rubrics:", error);
      showSnackbar(
        "Ett fel uppstod när bedömningsmatriserna skulle raderas",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!assignmentName.trim() || !assignmentDescription.trim()) {
      showSnackbar(
        "Vänligen fyll i både uppgiftens namn och beskrivning",
        "error",
      );
      return;
    }
    setIsGenerating(true);
    try {
      const response = await callApi(
        [
          {
            role: "system",
            content: `Du är en pedagogisk expert som specialiserar sig på att skapa bedömningsmatriser. 
            Din uppgift är att generera en detaljerad och användbar bedömningsmatris baserad på lärarens önskemål.
            Du ska svara med en JSON-struktur som kan användas direkt i vårt system.`,
          },
          {
            role: "user",
            content: `Skapa en bedömningsmatris för uppgiften: ${assignmentName}, med 
          
          uppgiftsbeskrivning: ${assignmentDescription}.
            Språket för matrisen ska vara på ${language}. 
            
            SVARA EXAKT I DETTA JSON-FORMAT:
            {
              "name": "Titel på matrisen",
              "criteria": [
                {
                  "name": "Kriterium 1",
                  "descriptors": [
                    {
                      "text": "Beskrivning för betyg A",
                      "grade": "A"
                    },
                    {
                      "text": "Beskrivning för betyg C",
                      "grade": "C"
                    },
                    {
                      "text": "Beskrivning för betyg E",
                      "grade": "E"
                    },
                    {
                      "text": "Beskrivning för betyg F",
                      "grade": "F"
                    }
                  ]
                }
              ]
            }
            
            Skapa minst 3 och max 5 kriterier. Varje kriterium ska ha 4 beskrivningar, en för varje betyg (A, C, E, F).
            Se till att beskrivningarna är tydliga, pedagogiska och relevanta för ämnet och årskursen.`,
          },
        ],
        6000,
        true,
        user.uid,
      );

      let messageContent = response.data.choices[0].message.content.trim();
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }

      const data = JSON.parse(messageContent);

      setRubricName(data.name);
      setCriteria(data.criteria);
      setView("form");

      await trackLesson(user.uid, "GenerateRubric.js", null, null);

      showSnackbar(
        "Matrisen har genererats. Glöm inte att spara den.",
        "success",
      );
    } catch (error) {
      console.error("Error generating rubric:", error);
      showSnackbar("Ett fel uppstod vid generering av matrisen", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddCriteria = () => {
    setCriteria([
      ...criteria,
      {
        name: "",
        descriptors: [
          { text: "", grade: "A" },
          { text: "", grade: "C" },
          { text: "", grade: "E" },
          { text: "", grade: "F" },
        ],
      },
    ]);
  };

  const handleRemoveCriteria = (index) => {
    const newCriteria = criteria.filter((_, i) => i !== index);
    setCriteria(newCriteria);
  };

  const handleAddDescriptor = (criteriaIndex) => {
    const newCriteria = [...criteria];
    newCriteria[criteriaIndex].descriptors.push({ text: "", grade: "A" });
    setCriteria(newCriteria);
  };

  const handleRemoveDescriptor = (criteriaIndex, descriptorIndex) => {
    const newCriteria = [...criteria];
    newCriteria[criteriaIndex].descriptors = newCriteria[
      criteriaIndex
    ].descriptors.filter((_, i) => i !== descriptorIndex);
    setCriteria(newCriteria);
  };

  const handleCriteriaChange = (index, value) => {
    const newCriteria = [...criteria];
    newCriteria[index].name = value;
    setCriteria(newCriteria);
  };

  const handleDescriptorChange = (
    criteriaIndex,
    descriptorIndex,
    field,
    value,
  ) => {
    const newCriteria = [...criteria];
    newCriteria[criteriaIndex] = {
      ...newCriteria[criteriaIndex],
      descriptors: newCriteria[criteriaIndex].descriptors.map(
        (descriptor, index) =>
          index === descriptorIndex
            ? { ...descriptor, [field]: value }
            : descriptor,
      ),
    };
    setCriteria(newCriteria);
  };
  const handleSaveRubric = async () => {
    if (rubricName.trim() === "") {
      showSnackbar("Vänligen fyll i namnet på bedömningsmatrisen", "error");
      return;
    }
    const isValid = criteria.every(
      (criterion) =>
        criterion.name.trim() !== "" &&
        criterion.descriptors.every(
          (descriptor) => descriptor.text.trim() !== "",
        ),
    );
    if (!isValid) {
      showSnackbar("Vänligen fyll i alla fält innan du sparar", "error");
      return;
    }

    setIsSaving(true);
    try {
      const docRef = await addDoc(collection(db, "rubrics"), {
        name: rubricName,
        custom: false,
        criteria: criteria,
        formativeComments: true,
        createdAt: new Date(),
        userId: user.uid,
        assignmentName: assignmentName || "", // Save the assignment name
        assignmentDescription: assignmentDescription || "", // Save the assignment description
      });

      showSnackbar("Bedömningsmatrisen har sparats", "success");
      navigate(`/app/matris/visa/${docRef.id}`);
    } catch (error) {
      console.error("Error saving rubric:", error);
      showSnackbar(
        "Ett fel uppstod när bedömningsmatrisen skulle sparas",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // const handleSaveRubric = async () => {
  //   // Check if all visible fields are filled
  //   if (rubricName.trim() === "") {
  //     showSnackbar("Vänligen fyll i namnet på bedömningsmatrisen", "error");
  //     return;
  //   }

  //   const isValid = criteria.every(
  //     (criterion) =>
  //       criterion.name.trim() !== "" &&
  //       criterion.descriptors.every(
  //         (descriptor) => descriptor.text.trim() !== "",
  //       ),
  //   );

  //   if (!isValid) {
  //     showSnackbar("Vänligen fyll i alla fält innan du sparar", "error");
  //     return;
  //   }

  //   setIsSaving(true);
  //   try {
  //     await addDoc(collection(db, "rubrics"), {
  //       name: rubricName,
  //       custom: false,
  //       criteria: criteria,
  //       formativeComments: true,
  //       createdAt: new Date(),
  //       userId: user.uid,
  //     });
  //     showSnackbar("Bedömningsmatrisen har sparats", "success");

  //     // Reset form
  //     setRubricName("");
  //     setCriteria([{ name: "", descriptors: [{ text: "", grade: "A" }] }]);
  //   } catch (error) {
  //     console.error("Error saving rubric:", error);
  //     showSnackbar(
  //       "Ett fel uppstod när bedömningsmatrisen skulle sparas",
  //       "error",
  //     );
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleExampleClick = () => {
    if (isExample) {
      // Reset the form
      setRubricName("");
      setCriteria([
        {
          name: "",
          descriptors: [
            { text: "", grade: "A" },
            { text: "", grade: "C" },
            { text: "", grade: "E" },
            { text: "", grade: "F" },
          ],
        },
      ]);
      setIsExample(false);
    } else {
      setRubricName("Argumenterande");
      setCriteria([
        {
          name: "Argumentation",
          descriptors: [
            {
              text: "Starka, väl underbyggda argument som övertygar läsaren. Motargument bemöts effektivt.",
              grade: "A",
            },
            {
              text: "Tydlig argumentation med stödjande exempel, men vissa argument kan utvecklas mer.",
              grade: "C",
            },
            {
              text: "Grundläggande argumentation, men vissa argument är svaga eller saknar stöd.",
              grade: "E",
            },
            {
              text: "Bristfälliga eller frånvarande argument. Ingen tydlig linje i resonemanget.",
              grade: "F",
            },
          ],
        },
        {
          name: "Disposition",
          descriptors: [
            {
              text: "Logisk och tydlig struktur med starka övergångar mellan idéer.",
              grade: "A",
            },
            {
              text: "Struktur finns, men vissa övergångar är otydliga.",
              grade: "C",
            },
            {
              text: "Acceptabel struktur, men vissa avsnitt är svåra att följa.",
              grade: "E",
            },
            {
              text: "Oorganiserad och svår att följa; ingen tydlig inledning, kropp eller avslutning.",
              grade: "F",
            },
          ],
        },
        {
          name: "Språk och stil",
          descriptors: [
            {
              text: "Välutvecklat språk, stilistiskt övertygande och engagerande.",
              grade: "A",
            },
            { text: "Bra språk med några mindre språkliga fel.", grade: "C" },
            {
              text: "Enkelt men begripligt språk med några fel som påverkar flytet.",
              grade: "E",
            },
            {
              text: "Många språkliga fel som försvårar förståelsen.",
              grade: "F",
            },
          ],
        },
        {
          name: "Källhantering",
          descriptors: [
            {
              text: "Effektiv och korrekt användning av källor som stärker argumenten.",
              grade: "A",
            },
            {
              text: "Källor används, men vissa är tveksamma eller saknas.",
              grade: "C",
            },
            {
              text: "Grundläggande användning av källor, men vissa referenser saknas eller är felaktiga.",
              grade: "E",
            },
            { text: "Ingen eller felaktig användning av källor.", grade: "F" },
          ],
        },
      ]);
      setIsExample(true);
    }
  };

  const gradeColors = {
    A: "#2E7D32",
    B: "#1976D2",
    C: "#FFA000",
    D: "#ED6C02",
    E: "#D32F2F",
    F: "#757575",
  };

  return (
    <Box sx={{ ...styles.container }}>
      <Box sx={styles.innerContainer}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 6,
          }}
        >
          {view !== "selection" && (
            <IconButton
              onClick={() => {
                setView("selection");
                setAiPrompt("");
              }}
              sx={{
                position: "absolute",
                top: { xs: "60px", md: "18px" },
                left: { xs: "10px", md: "0px" },
                fontSize: { xs: "0.9rem", md: "1rem" },
                ...styles.iconButtonBg,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant="h1"
            gutterBottom
            sx={{
              ...styles.newH1,
              width: { xs: "100%", md: "100%" },
              mt: { xs: 0, md: "inherit" },
            }}
          >
            Skapa ny matris
          </Typography>
        </Box>
        {user.admin && (
          <>
            <Box sx={{ mb: 4, display: "none" }}>
              <Button
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                variant="print"
                onClick={handleAddAllRubrics}
                sx={{ marginTop: 2, marginRight: 2 }}
                disabled={isSaving}
              >
                {isSaving ? "Sparar..." : "Lägg till alla matriser"}
              </Button>

              <Button
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DeleteIcon />
                  )
                }
                variant="print"
                onClick={handleDeleteAllRubrics}
                sx={{ marginTop: 2 }}
                disabled={isSaving}
              >
                {isSaving ? "Raderar..." : "Radera alla matriser"}
              </Button>
            </Box>
          </>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {location.search === "?ref=formative" ? (
            <Button
              variant="contained"
              color="primary"
              component={Link}
              startIcon={<ArrowBackIcon />}
              to="/app/bedomning/formativ-bedomning"
            >
              Tillbaka till formativ bedömning
            </Button>
          ) : (
            <>
              {!isMobile && (
                <>
                  <Tooltip title="Ny bedömning">
                    <IconButton
                      onClick={() => navigate("/app/uppsats/ny")}
                      sx={{
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        ...styles.iconButtonBg,
                      }}
                    >
                      <AddIcon sx={{ color: "text.secondary" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Mina bedömningar">
                    <IconButton
                      onClick={() => navigate("/app/uppsatser")}
                      sx={{
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        ...styles.iconButtonBg,
                        ml: 1,
                      }}
                    >
                      <SegmentIcon sx={{ color: "text.secondary" }} />
                    </IconButton>
                  </Tooltip>

                  {/*
                  <Button
                    variant="purp"
                    sx={{ ...styles.tightButton }}
                    component={Link}
                    startIcon={<AddIcon />}
                    to="/app/uppsats/ny"
                  >
                    Ny bedömning
                  </Button>
                  <Button
                    variant="purp"
                    sx={{ ...styles.tightButton }}
                    component={Link}
                    startIcon={<SegmentIcon />}
                    to="/app/uppsatser"
                  >
                    Mina bedömningar
                  </Button>
                  */}
                </>
              )}
            </>
          )}
        </Box>

        {/* Selection screen */}
        {view === "selection" && (
          <>
            <AnimatedStep isRevealed={true}>
              <Box
                sx={{
                  ...styles.headlineLessonBox,
                  mt: { xs: 0, md: 10 },
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ ...styles.headlineLessonBoxTitle }}
                >
                  Hur vill du skapa din matris?
                </Typography>
              </Box>
              <Box
                sx={{
                  ...styles.freshBox,
                  padding: "40px",
                  width: { xs: "100%", md: "100%" },
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                <Grid container spacing={3} sx={{}}>
                  <Grid item xs={12} md={6}>
                    <ThemeFlowBox
                      icon={AutoAwesomeIcon}
                      title="Generera med AI"
                      subtitle="Låt AI generera en färdig matris baserad på dina önskemål. Redigera och spara."
                      onClick={() => setView("aiForm")}
                      diffView={true}
                      height="220px"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ThemeFlowBox
                      icon={CreateIcon}
                      title="Skapa manuellt"
                      subtitle="Skapa din egen matris från en tom mall där du själv skriver in kriterier & krav."
                      onClick={() => {
                        setRubricName("");
                        setCriteria([
                          {
                            name: "",
                            descriptors: [
                              { text: "", grade: "A" },
                              { text: "", grade: "C" },
                              { text: "", grade: "E" },
                              { text: "", grade: "F" },
                            ],
                          },
                        ]);
                        setView("form");
                      }}
                      diffView={true}
                      height="220px"
                    />
                  </Grid>
                </Grid>
              </Box>
            </AnimatedStep>
          </>
        )}

        {/* AI Generation Form */}
        {view === "aiForm" && (
          <>
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox }}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  Beskriv uppgiften
                </Typography>
              </Box>
              <Box
                sx={{
                  ...styles.freshBox,
                  ...styles.softBoxShadow,
                  padding: "40px",
                  width: { xs: "100%", md: "70%" },
                  margin: "0 auto",
                }}
              >
                <Box sx={{}}>
                  <Box>
                    <Typography
                      variant="formLabelSinUnderline"
                      sx={{
                        fontWeight: "500",
                        display: "block",
                      }}
                    >
                      Uppgiftens namn *
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="T.ex. bokrecension, Muntlig presentation, Laborationsrapport"
                      value={assignmentName}
                      onChange={(e) => setAssignmentName(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      sx={{ mt: 1, mb: 4 }}
                      autoFocus={!isMobile}
                    />

                    <Typography
                      variant="formLabelSinUnderline"
                      sx={{
                        fontWeight: "500",
                        display: "block",
                      }}
                    >
                      Uppgiftsbeskrivning *
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Skriv och presentera en bokrecension om en godkänd roman, skriv en argumenterande text om skoluniformer, etc."
                      value={assignmentDescription}
                      onChange={(e) => setAssignmentDescription(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      multiline
                      minRows={4}
                    />

                    <Typography
                      variant="formLabelSinUnderline"
                      sx={{
                        fontWeight: "500",
                        display: "block",
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      Språk
                    </Typography>
                    <LanguageSelector
                      language={language}
                      setLanguage={setLanguage}
                    />

                    <Box
                      sx={{
                        mt: 5,
                        textAlign: "center",
                      }}
                    >
                      <Button
                        variant="purp"
                        disabled={isGenerating}
                        onClick={handleGenerateWithAI}
                        startIcon={
                          isGenerating ? (
                            <CircularProgress
                              sx={{ color: "white" }}
                              size={20}
                            />
                          ) : (
                            <AutoAwesomeIcon />
                          )
                        }
                      >
                        {isGenerating ? "Genererar..." : "Generera matris"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AnimatedStep>
          </>
        )}

        {/* Regular Form - Your existing form */}
        {view === "form" && (
          <Box
            sx={{
              bgcolor: "background.default",
            }}
          >
            {assignmentName && assignmentDescription && (
              <>
                <Alert
                  severity="info"
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderRadius: 4,
                    backgroundColor: theme.palette.mode === "dark" ? "" : "",
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid #3b4651"
                        : "1px solid #c0e4f3",
                    alignItems: "center", // This centers the icon vertically with the text
                    "& .MuiAlert-icon": {
                      fontSize: "1.8rem", // Adjust size as needed
                    },
                  }}
                >
                  <span style={{ fontWeight: "500", fontSize: "1rem" }}>
                    Gör valfria ändringar om du vill, klicka sedan på{" "}
                    <span style={{ fontWeight: 700 }}> Spara</span> längst ned.
                  </span>
                </Alert>
              </>
            )}

            {user.admin && (
              <>
                {!aiPrompt && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-end",
                      mb: 2,
                    }}
                  >
                    <Button
                      onClick={handleExampleClick}
                      variant="outlined"
                      sx={{
                        ...styles.beautyBtn,
                        ...styles.exampleBtn,
                      }}
                    >
                      {isExample ? "Nollställ" : "Visa exempel"}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Criteria Section */}
            {/* Criteria Section - Grid Layout */}
            <Grid container spacing={4} sx={{ mb: 3 }}>
              {criteria.map((criterion, criteriaIndex) => (
                <>
                  <Grid item xs={12} sm={12} md={6} key={criteriaIndex}>
                    <Paper
                      elevation={3}
                      sx={{
                        ...styles.freshBox,
                        p: 3,
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                        backgroundImage: "none",
                        position: "relative",
                        height: "100%",
                      }}
                    >
                      {/* Header with criterion name and delete button */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "stretch", md: "center" },
                          mb: 3.5,
                          gap: { xs: 1, md: 2 },
                        }}
                      >
                        {/* Top row on mobile: Criterion title and delete button */}
                        <Box
                          sx={{
                            display: { xs: "flex", md: "contents" },
                            justifyContent: {
                              xs: "space-between",
                              md: "initial",
                            },
                            alignItems: { xs: "center", md: "initial" },
                            gap: { xs: 1, md: 0 },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "600",
                              minWidth: { xs: "auto", md: "120px" },
                              flexShrink: 0,
                            }}
                          >
                            Kriterium {criteriaIndex + 1}
                          </Typography>

                          {/* Delete button - visible on mobile in top row */}
                          <Tooltip title="Ta bort kriterium">
                            <IconButton
                              onClick={() =>
                                handleRemoveCriteria(criteriaIndex)
                              }
                              color="text.secondary"
                              size="small"
                              sx={{
                                display: { xs: "flex", md: "none" },
                                flexShrink: 0,
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {/* Text field - full width on mobile */}
                        <TextField
                          fullWidth
                          placeholder={`Namn på kriterium, t.ex. Språk och stil`}
                          value={criterion.name}
                          onChange={(e) =>
                            handleCriteriaChange(criteriaIndex, e.target.value)
                          }
                          variant="outlined"
                          autoFocus={!isMobile}
                          InputProps={{
                            sx: {
                              "& input": {
                                fontWeight: 400,
                                fontSize: { xs: "0.95rem", md: "1.1rem" },
                              },
                            },
                          }}
                          sx={{
                            order: { xs: 2, md: 0 },
                          }}
                        />

                        {/* Delete button - visible on desktop */}
                        <Tooltip title="Ta bort kriterium">
                          <IconButton
                            onClick={() => handleRemoveCriteria(criteriaIndex)}
                            color="text.secondary"
                            size="small"
                            sx={{
                              display: { xs: "none", md: "flex" },
                              flexShrink: 0,
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/* Descriptors in compact format */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {criterion.descriptors
                          .sort((a, b) => {
                            const grades = { A: 0, C: 1, E: 2, F: 3 };
                            return grades[a.grade] - grades[b.grade];
                          })
                          .map((descriptor, descriptorIndex) => (
                            <Box
                              key={descriptorIndex}
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 1, md: 1.5 },
                                p: 2,
                                backgroundColor: getGradeColor(
                                  descriptor.grade,
                                ),
                                borderRadius: 4,
                                alignItems: { xs: "stretch", md: "flex-start" },
                              }}
                            >
                              {/* Top row on mobile: Grade selector and delete button */}
                              <Box
                                sx={{
                                  display: { xs: "flex", md: "contents" },
                                  justifyContent: {
                                    xs: "space-between",
                                    md: "initial",
                                  },
                                  alignItems: { xs: "center", md: "initial" },
                                  gap: { xs: 1, md: 0 },
                                }}
                              >
                                {/* Grade Chip */}
                                <Select
                                  value={descriptor.grade}
                                  onChange={(e) =>
                                    handleDescriptorChange(
                                      criteriaIndex,
                                      descriptorIndex,
                                      "grade",
                                      e.target.value,
                                    )
                                  }
                                  size="small"
                                  sx={{
                                    minWidth: { xs: "80px", md: "60px" },
                                    flexShrink: 0,
                                    borderRadius: 4,
                                    fontWeight: "bold",
                                    backgroundColor:
                                      gradeColors[descriptor.grade],
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor:
                                        gradeColors[descriptor.grade] ||
                                        gradeColors.F,
                                      borderWidth: "2px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor:
                                          gradeColors[descriptor.grade] ||
                                          gradeColors.F,
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor:
                                          gradeColors[descriptor.grade] ||
                                          gradeColors.F,
                                      },
                                    color: "white",
                                    "& .MuiSelect-select": {
                                      py: { xs: 1, md: 2 },
                                    },
                                    "& .MuiSelect-icon": {
                                      color: "white",
                                    },
                                  }}
                                >
                                  <MenuItem value="A">A</MenuItem>
                                  <MenuItem value="C">C</MenuItem>
                                  <MenuItem value="E">E</MenuItem>
                                  <MenuItem value="F">F</MenuItem>
                                </Select>

                                {/* Delete descriptor button - visible on mobile in top row */}
                                <Tooltip title="Ta bort betyg">
                                  <IconButton
                                    onClick={() =>
                                      handleRemoveDescriptor(
                                        criteriaIndex,
                                        descriptorIndex,
                                      )
                                    }
                                    color="text.secondary"
                                    size="small"
                                    sx={{
                                      display: { xs: "flex", md: "none" },
                                      flexShrink: 0,
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>

                              {/* Description Input - full width on mobile */}
                              <TextField
                                fullWidth
                                placeholder="Beskrivning för detta betyg..."
                                value={descriptor.text}
                                onChange={(e) =>
                                  handleDescriptorChange(
                                    criteriaIndex,
                                    descriptorIndex,
                                    "text",
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                multiline
                                minRows={1}
                                maxRows={4}
                                sx={{
                                  ...styles.whiteBg,
                                  order: { xs: 2, md: 0 },
                                }}
                              />

                              {/* Delete descriptor button - visible on desktop */}
                              <Tooltip title="Ta bort betyg">
                                <IconButton
                                  onClick={() =>
                                    handleRemoveDescriptor(
                                      criteriaIndex,
                                      descriptorIndex,
                                    )
                                  }
                                  color="text.secondary"
                                  size="small"
                                  sx={{
                                    display: { xs: "none", md: "flex" },
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}

                        {/* Add descriptor button */}
                        <Button
                          startIcon={<AddIcon />}
                          variant="print"
                          onClick={() => handleAddDescriptor(criteriaIndex)}
                          size="small"
                          sx={{ mt: 2 }}
                        >
                          Lägg till betyg
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </>
              ))}

              {/* Add criterion button as grid item */}
              <Grid item xs={12} md={6}>
                <DashedPaper
                  onClick={handleAddCriteria}
                  sx={{ minHeight: { xs: "200px", md: "300px" } }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <AddIcon
                      sx={{
                        fontSize: 48,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Lägg till kriterium
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    ></Typography>
                  </Box>
                </DashedPaper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} />
            {/* Name Card */}
            <Paper
              elevation={3}
              sx={{
                p: 3,
                margin: "0 auto",
                mb: 3,
                borderRadius: 3,
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                backgroundImage: "none",
                width: { xs: "100%", md: "50%" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    minWidth: "120px",
                    color: "text.primary",
                  }}
                >
                  Namn på matrisen:
                </Typography>
                <TextField
                  placeholder="Namn på matrisen"
                  value={rubricName}
                  onChange={(e) => setRubricName(e.target.value)}
                  sx={{
                    flexGrow: 1,
                  }}
                />
              </Box>
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                variant={theme.palette.mode === "light" ? "purp" : "contained"}
                onClick={handleSaveRubric}
                disabled={isSaving}
                sx={{ mt: 3, width: { xs: "100%", md: "300px" } }}
              >
                {isSaving ? "Sparar..." : "Spara matris"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <LoadingDialog
        open={isGenerating}
        onClose={() => {}} // You can add a function here if needed
        progressLabel={"Genererar matris — tar 10-20 sekunder"}
        title="Genererar matris. Snart klart..."
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        hideProgressBar={true}
        coffee={false}
      />
    </Box>
  );
};

export default Rubrics;
